import { Module } from 'vuex'

interface PracticeProgress {
  userAnswers: Record<number, number[]>;
  questionStatus: Record<number, 'correct' | 'wrong'>;
  markedQuestions: number[];
  currentQuestionIndex: number;
}

interface PracticeState {
  practiceCache: Record<string, PracticeProgress>;
  currentLang: 'zh' | 'jp';
  // 倒计时核心状态
  endTime: number;
  timer: ReturnType<typeof setInterval> | null;
  countdown: {
    hours: number;
    minutes: number;
    seconds: number;
  };
}

export const Practice: Module<PracticeState, unknown> = {
  namespaced: true,
  state: () => ({
    practiceCache: {},
    currentLang: (localStorage.getItem('practice_lang') as 'zh' | 'jp') || 'zh',
    // 初始化时从本地存储恢复时间，防止刷新变0
    endTime: Number(localStorage.getItem('practice_end_time')) || 0,
    timer: null,
    countdown: { hours: 0, minutes: 0, seconds: 0 }
  }),
  getters: {
    getCountdown: (state) => state.countdown,
    getEndTime: (state) => state.endTime
  },
  mutations: {
    SET_PRACTICE_CACHE(state, { randomId, data }) {
      state.practiceCache[randomId] = { ...data };
    },
    CLEAR_PRACTICE_CACHE(state, randomId) {
      if (state.practiceCache[randomId]) {
        delete state.practiceCache[randomId];
      }
    },
    CLEAR_ALL(state) {
      if (state.timer) clearInterval(state.timer);
      state.practiceCache = {};
      state.currentLang = 'zh';
      state.endTime = 0;
      state.timer = null;
      state.countdown = { hours: 0, minutes: 0, seconds: 0 };
    },
    SET_LANGUAGE(state, lang: 'zh' | 'jp') {
      state.currentLang = lang;
      localStorage.setItem('practice_lang', lang);
    },
    // 倒计时 Mutations
    SET_END_TIME(state, time: number) {
      state.endTime = time;
      localStorage.setItem('practice_end_time', time.toString());
    },
    SET_COUNTDOWN(state, data) {
      state.countdown = data;
    },
    SET_TIMER(state, timer) {
      state.timer = timer;
    },
    CLEAR_TIMER_STATE(state) {
      if (state.timer) clearInterval(state.timer);
      state.timer = null;
      state.endTime = 0;
      state.countdown = { hours: 0, minutes: 0, seconds: 0 };
      localStorage.removeItem('practice_end_time');
    }
  },
  actions: {
    updatePractice({ commit }, payload: { randomId: string, data: PracticeProgress }) {
      commit('SET_PRACTICE_CACHE', payload);
    },
    removePractice({ commit }, randomId: string) {
      commit('CLEAR_PRACTICE_CACHE', randomId);
    },
    changeLanguage({ commit }, lang: 'zh' | 'jp') {
      commit('SET_LANGUAGE', lang);
    },
    // 倒计时 Actions
    initTimer({ commit, dispatch }, minutes: number) {
      const endTime = Date.now() + minutes * 60 * 1000;
      commit('SET_END_TIME', endTime);
      dispatch('startTimer');
    },
    startTimer({ state, commit, dispatch }) {
      if (state.timer) clearInterval(state.timer);
      
      const update = () => {
        const now = Date.now();
        const diff = state.endTime - now;

        if (diff <= 0) {
          commit('SET_COUNTDOWN', { hours: 0, minutes: 0, seconds: 0 });
          dispatch('stopTimer');
          return;
        }

        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        commit('SET_COUNTDOWN', { hours, minutes, seconds });
      };

      update();
      const timer = setInterval(update, 1000);
      commit('SET_TIMER', timer);
    },
    stopTimer({ state, commit }) {
      if (state.timer) clearInterval(state.timer);
      commit('SET_TIMER', null);
    },
    clearTimer({ commit }) {
      commit('CLEAR_TIMER_STATE');
    }
  }
}